import * as React from "react"
import { Search, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "./input"
import { Button } from "./button"

export interface FilterBarProps {
  search: string
  onSearchChange: (value: string) => void
  placeholder?: string
  advancedFilters?: React.ReactNode  // Popover content with filters
  onClearAll?: () => void
  activeFilterCount?: number
  className?: string
}

export function FilterBar({
  search,
  onSearchChange,
  placeholder = "Search...",
  advancedFilters,
  onClearAll,
  activeFilterCount = 0,
  className
}: FilterBarProps) {
  const [localSearch, setLocalSearch] = React.useState(search)
  const timeoutRef = React.useRef<NodeJS.Timeout>()

  // Sync local state with prop changes
  React.useEffect(() => {
    setLocalSearch(search)
  }, [search])

  // Debounced search (300ms)
  const handleSearchChange = (value: string) => {
    setLocalSearch(value)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      onSearchChange(value)
    }, 300)
  }

  // Immediate search on Enter key (skip debounce)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Clear pending debounce
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      // Trigger search immediately - use currentTarget.value to get current input value
      // (not localSearch state which might not have updated yet if user typed then immediately pressed Enter)
      onSearchChange(e.currentTarget.value)
    }
  }

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const hasActiveFilters = activeFilterCount > 0 || search.length > 0

  return (
    <div
      className={cn(
        // Wrap on narrow viewports so search input, advanced filters, and Clear
        // don't get crushed at < sm.
        "flex flex-wrap items-center gap-2 sm:gap-3 p-4 bg-white border-b border-gray-200",
        className
      )}
    >
      {/* Search Input */}
      <div className="relative flex-1 min-w-[200px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          type="text"
          placeholder={placeholder}
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="pl-9"
        />
      </div>

      {/* Advanced Filters (optional) */}
      {advancedFilters && advancedFilters}

      {/* Clear All Button (only show if filters active) */}
      {hasActiveFilters && onClearAll && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-gray-600 hover:text-gray-900"
        >
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      )}
    </div>
  )
}
